/**
 * Option strategy attached to an NB / NS / BB / BS price level — opens (PAPER for now)
 * when the level fires and the global Auto-Execute switch is ON.
 * Backend: controllers/levelOptionTrigger.controller.ts.
 */
import api from './api';

export type LevelKey = 'nbuy' | 'nshort' | 'bbuy' | 'bshort';
export type ExpiryTier = 'D0' | 'D1' | 'W0' | 'M0';

export interface LevelOptionTrigger {
  id: number; userId: number; instrumentId: number; levelKey: LevelKey; levelValue: number | null;
  templateId: string; expiryTier: ExpiryTier; minHoursToExpiry: number | null;
  lots: number; maxRiskInr: number | null;
  tpAmountInr: number | null; slAmountInr: number | null; tgtUnderlying: number | null; slUnderlying: number | null;
  autoTrail: boolean; trailArmPct: number | null; trailLockPct: number | null; preExpiryExitMins: number | null;
  maxSpreadPct: number; maxChasePct: number; tradingMode: 'PAPER' | 'LIVE';
  /** A specific expiry date (YYYY-MM-DD) instead of the tier; null = the tier rule. */
  expiryIso?: string | null;
  /** The user's edited legs; null = the template picks the strikes at the fire. */
  customLegs?: Array<{ action: 'BUY' | 'SELL'; t: 'CE' | 'PE'; strike: number; offset: number; lots: number }> | null;
  strikeMode?: 'FIXED' | 'OFFSET' | null;
  status: 'ARMED' | 'FIRING' | 'FIRED' | 'FIRED_PARTIAL' | 'FAILED' | 'CANCELLED' | 'SUPERSEDED';
  failCount: number; firedAt: string | null; basketId: number | null; lastError: string | null;
  createdAt: string; updatedAt: string;
}

export interface LevelOptionState {
  attachments: LevelOptionTrigger[];
  levels: Record<LevelKey, number | null>;
  /** Cross-arming per level: { armed, at } — `at` is the level value the decision refers to. */
  arm: Partial<Record<LevelKey, { armed: boolean; at: number }>> | null;
  autoExecute: boolean;
}

export type ArmBody = Partial<Omit<LevelOptionTrigger, 'id' | 'userId' | 'status' | 'failCount' | 'firedAt' | 'basketId' | 'lastError' | 'createdAt' | 'updatedAt' | 'levelValue'>> & {
  instrumentId: number; levelKey: LevelKey; templateId: string;
  /** LIVE only — the typed word LIVE (the server refuses a LIVE arm without it). */
  confirmLive?: string;
};

const quiet = { headers: { _suppressToast: 'true' } as any };

export const levelOptionTriggersService = {
  async state(instrumentId: number): Promise<LevelOptionState> {
    const r = await api.get<any>('/options/level-triggers/state', { params: { instrumentId }, ...quiet });
    return r.data?.data;
  },
  async list(history = false): Promise<LevelOptionTrigger[]> {
    const r = await api.get<any>('/options/level-triggers', { params: history ? { history: '1' } : {}, ...quiet });
    return r.data?.data || [];
  },
  async arm(body: ArmBody): Promise<LevelOptionTrigger> {
    const r = await api.post<any>('/options/level-triggers', body, quiet);
    return r.data?.data;
  },
  async cancel(id: number): Promise<void> {
    await api.delete(`/options/level-triggers/${id}`, quiet);
  },
};

/** Underlyings that can carry an option strategy on a level (Delta BTC / ETH options). */
export const levelOptionCapable = (symbol: string | null | undefined, exchange: string | null | undefined) =>
  String(exchange || '').toUpperCase() === 'DELTA' && /^(BTC|ETH)USD$/.test(String(symbol || '').toUpperCase());

/** "is this level armed?" from the backend's cross-arming record: unknown → armed (the
 *  backend decides on its next observation); a record for another value is stale. */
export function levelArmed(arm: LevelOptionState['arm'] | null | undefined, k: LevelKey, level: number | null | undefined): boolean | null {
  if (level == null) return null;
  const a = arm?.[k];
  if (!a || Number(a.at) !== Number(level)) return null;
  return a.armed === true;
}

export const LEVEL_SHORT: Record<LevelKey, string> = { nbuy: 'NB', nshort: 'NS', bbuy: 'BB', bshort: 'BS' };
export const TIER_LABEL: Record<ExpiryTier, string> = { D0: 'Today', D1: 'Tomorrow', W0: 'This week', M0: 'This month' };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateShort = (iso: string) => `${Number(iso.slice(8, 10))} ${MON[Number(iso.slice(5, 7)) - 1] || ''}`;
/** "this week expiry" / "6 Nov expiry" (+ "your legs" when the strikes were edited). */
export const attExpiryText = (a: Pick<LevelOptionTrigger, 'expiryTier' | 'expiryIso' | 'customLegs'>) =>
  (a.expiryIso ? `${dateShort(a.expiryIso)} expiry` : `${TIER_LABEL[a.expiryTier].toLowerCase()} expiry`) + (a.customLegs && a.customLegs.length ? ', your legs' : '');
/** Compact form for chips / axis labels: "W0" or "6 Nov" (+ "✎" for edited legs). */
export const attExpiryShort = (a: Pick<LevelOptionTrigger, 'expiryTier' | 'expiryIso' | 'customLegs'>) =>
  (a.expiryIso ? dateShort(a.expiryIso) : a.expiryTier) + (a.customLegs && a.customLegs.length ? '✎' : '');
